// Admin UsersPage — every account in one place. Role tab, search text and page are held in the URL
// (?role=, ?q=, ?page=) so links, refresh and the back button work. Search runs on the server.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../../hooks/useApi.js";
import { useAuth } from "../../hooks/useAuth.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import UserRoleTabs from "../../components/admin/UserRoleTabs.js";
import UsersTable from "../../components/admin/UsersTable.js";
import { ROLES } from "@dm-lms/shared/types";
import type { AdminUserListItem, PaginatedResult, Role } from "@dm-lms/shared/types";
import { USER_SEARCH_MAX_LENGTH } from "@dm-lms/shared/constants";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface ListState {
  role: Role | undefined;
  q:    string;
  page: number;
}

function parseRole(value: string | null): Role | undefined {
  return ROLES.find((role) => role === value);
}

function parsePage(value: string | null): number {
  const page = Number.parseInt(value ?? "", 10);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

export default function UsersPage() {
  const [params, setParams] = useSearchParams();
  const { user: me } = useAuth();

  const role = parseRole(params.get("role"));
  const urlQ = (params.get("q") ?? "").trim();
  const page = parsePage(params.get("page"));

  // The single way the URL changes: an empty q or page 1 is left out, so default URLs stay clean.
  function setList(next: ListState, replace = false) {
    const sp = new URLSearchParams();
    if (next.role) sp.set("role", next.role);
    if (next.q) sp.set("q", next.q);
    if (next.page > 1) sp.set("page", String(next.page));
    setParams(sp, { replace });
  }

  // The text box shows what is typed; the URL (and the request) follow 300 ms after typing stops.
  const [draft, setDraft] = useState(urlQ);
  const debouncedDraft = useDebouncedValue(draft.trim(), SEARCH_DEBOUNCE_MS);
  const lastWrittenQ = useRef(urlQ);

  useEffect(() => {
    if (debouncedDraft === urlQ) return;
    lastWrittenQ.current = debouncedDraft;
    setList({ role, q: debouncedDraft, page: 1 }, true);
    // Runs only when the debounced text changes; the URL values are read from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDraft]);

  // Back/forward changed q from outside: show it in the box.
  useEffect(() => {
    if (urlQ === lastWrittenQ.current) return;
    lastWrittenQ.current = urlQ;
    setDraft(urlQ);
  }, [urlQ]);

  const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (role) query.set("role", role);
  if (urlQ) query.set("q", urlQ);

  const { data, isLoading, error, refetch } = useFetch<PaginatedResult<AdminUserListItem>>(`/api/users?${query.toString()}`);

  // Step back a page when the last row of a later page was just deleted.
  function handleDeleted() {
    if (page > 1 && data?.items.length === 1) setList({ role, q: urlQ, page: page - 1 });
    else refetch();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Users</h1>

      <UserRoleTabs active={role} onSelect={(next) => { setList({ role: next, q: urlQ, page: 1 }); }} />

      <input
        type="search"
        placeholder="Search by name, email or code…"
        aria-label="Search users"
        maxLength={USER_SEARCH_MAX_LENGTH}
        value={draft}
        onChange={(event) => { setDraft(event.target.value); }}
        className="mb-4 w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
      />

      {isLoading && !data && <p className="text-dm-slate text-sm">Loading users…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!error && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-dm-slate text-sm">{urlQ ? "No users match your search." : "No users found."}</p>
          ) : (
            <UsersTable role={role} items={data.items} meId={me?.id} onDeleted={handleDeleted} dimmed={isLoading} />
          )}

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => { setList({ role, q: urlQ, page: Math.max(1, page - 1) }); }}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">Page {data.page} · {data.total} total</span>
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => { setList({ role, q: urlQ, page: page + 1 }); }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
